/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — shell do painel analítico read-only
 */
import { useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftIcon, BuildingOffice2Icon, ArrowLeftOnRectangleIcon, ArrowPathIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline'
import { AuthContext } from '../../../Context'
import { AuthContextApi } from '../../../Context/api'
import Header from '../../../Components/Header'
import { TABS, ADMIN_TABS, INVESTOR_UTILITY_TABS, TAB_THEME, TAB_DESCRIPTION } from './config/areaAnalytics'
import { formatDate } from '../../../utils/obraAnalytics'
import { useObraResumo, useDatasExibicaoInvestidor, useDatasExibicaoAdmin } from './hooks/useObraAnalytics'
import DataExibicaoPicker from './components/DataExibicaoPicker'
import ExecutivoTab from './tabs/ExecutivoTab'
import EstoqueTab from './tabs/EstoqueTab'
import VendasTab from './tabs/VendasTab'
import RecebidosTab from './tabs/RecebidosTab'
import AReceberTab from './tabs/AReceberTab'
import MovimentacaoTab from './tabs/MovimentacaoTab'
import ConfiguracaoBuscaTab from './tabs/ConfiguracaoBuscaTab'
import InvestidoresObraTab from './tabs/InvestidoresObraTab'
import RelatorioObraTab from './tabs/RelatorioObraTab'

const ObraPainel = () => {
    const { setters } = useContext(AuthContext)
    const { postObrasProcessarDados } = useContext(AuthContextApi)
    const navigate = useNavigate()
    const location = useLocation()
    const { id: empreendimentoId, obraId: obraIdParam } = useParams()
    const isInvestorRoute = location.pathname.startsWith('/investidor/')
    const activeObraId = obraIdParam

    const [tab, setTab] = useState(() => (isInvestorRoute ? 'executivo' : 'investidores-obra'))
    const [selectedDataId, setSelectedDataId] = useState('')
    const [period, setPeriod] = useState({ inicio: '', fim: '' })
    const [periodConfirmed, setPeriodConfirmed] = useState(false)
    const [processando, setProcessando] = useState(false)
    const isUtilityTab = isInvestorRoute
        ? tab === 'relatorios-obra'
        : (tab === 'investidores-obra' || tab === 'relatorios-obra' || tab === 'busca')

    const { datas: datasInvestidor, loading: loadingInvestidor } = useDatasExibicaoInvestidor(isInvestorRoute)
    const { datas: datasAdmin, loading: loadingAdmin } = useDatasExibicaoAdmin(!isInvestorRoute)
    const datasExibicao = isInvestorRoute ? datasInvestidor : datasAdmin
    const loadingDatas = isInvestorRoute ? loadingInvestidor : loadingAdmin

    const onSelectData = (item) => {
        if (!item) {
            setSelectedDataId('')
            setPeriod({ inicio: '', fim: '' })
            return
        }
        setSelectedDataId(String(item.id))
        setPeriod({ inicio: item.data_inicial, fim: item.data_final })
    }

    const confirmPeriod = () => {
        if (period.inicio && period.fim) setPeriodConfirmed(true)
    }

    const applyPeriodSearch = () => {
        if (period.inicio && period.fim) setPeriodConfirmed(true)
    }

    const hasPeriod = Boolean(period.inicio && period.fim)
    const canShowAnalytics = periodConfirmed && hasPeriod
    const needsPeriodGate = !isUtilityTab && !canShowAnalytics

    const resumoParams = hasPeriod && periodConfirmed ? { inicio: period.inicio, fim: period.fim } : {}
    const { data: resumo } = useObraResumo(activeObraId, isInvestorRoute, resumoParams, {
        requirePeriod: false,
        enabled: Boolean(activeObraId),
    })
    const obraInfo = resumo?.obra
    const empreendimentoLabel = obraInfo?.empreendimento
        ? `${obraInfo.empreendimento}${obraInfo.codigo_empreendimento ? ` (cód. ${obraInfo.codigo_empreendimento})` : ''}`
        : `Empreendimento ${empreendimentoId}`
    const obraLabel = obraInfo?.nome
        ? `${obraInfo.nome}${obraInfo.codigo ? ` · ${obraInfo.codigo}` : ''}`
        : `Obra ${activeObraId}`

    const visibleTabs = useMemo(() => (
        isInvestorRoute ? [...TABS, ...INVESTOR_UTILITY_TABS] : [...TABS, ...ADMIN_TABS]
    ), [isInvestorRoute])
    const theme = TAB_THEME[tab]
    const activeTab = useMemo(() => visibleTabs.find((t) => t.id === tab), [visibleTabs, tab])

    const periodLabel = hasPeriod
        ? `${formatDate(period.inicio)} a ${formatDate(period.fim)}`
        : 'Período não definido'

    const mesBR = (m) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : '—')
    const cobertura = resumo?.cobertura
    const coberturaItens = cobertura
        ? [
            { k: 'vendas', label: 'Vendas' },
            { k: 'recebidos', label: 'Recebidos' },
            { k: 'movimentacao', label: 'Movimentação' },
        ].filter(({ k }) => cobertura[k]?.min)
        : []

    const logoutInvestidor = () => {
        setters.setSignedInvestidor(false)
        navigate('/investidor/login')
    }

    const abrirModalProcessarDados = () => {
        setters.setPerigoModalMsg(true)
        setters.setTitleButtonCloseModalMsg('Cancelar')
        setters.setMsgModalMsg('Processar dados da obra?')
        setters.setDescModalMsg('Isso irá sincronizar os dados desta obra com o ERP. O processamento roda em segundo plano e pode levar alguns minutos.')
        setters.setTitleButtonModalMsg('Processar')
        setters.setFunctionModalMsg(() => async () => {
            setProcessando(true)
            const ok = await postObrasProcessarDados(activeObraId)
            setProcessando(false)
            if (ok) setters.setModalMsg(false)
        })
        setters.setModalMsg(true)
    }

    const renderTab = () => {
        switch (tab) {
            case 'executivo': return <ExecutivoTab obraId={activeObraId} investidor={isInvestorRoute} onNavigate={setTab} period={period} />
            case 'estoque': return <EstoqueTab obraId={activeObraId} investidor={isInvestorRoute} period={period} />
            case 'vendas': return <VendasTab obraId={activeObraId} investidor={isInvestorRoute} period={period} />
            case 'recebidos': return <RecebidosTab obraId={activeObraId} investidor={isInvestorRoute} period={period} />
            case 'a-receber': return <AReceberTab obraId={activeObraId} investidor={isInvestorRoute} period={period} />
            case 'movimentacao': return <MovimentacaoTab obraId={activeObraId} investidor={isInvestorRoute} period={period} />
            case 'busca': return <ConfiguracaoBuscaTab obraId={activeObraId} />
            case 'investidores-obra': return <InvestidoresObraTab obraId={activeObraId} />
            case 'relatorios-obra': return (
                <RelatorioObraTab
                    obraId={activeObraId}
                    obraCodigo={obraInfo?.codigo}
                    obraNome={obraInfo?.nome}
                    investidor={isInvestorRoute}
                />
            )
            default: return null
        }
    }

    const pickerProps = {
        datas: datasExibicao,
        loading: loadingDatas,
        selectedId: selectedDataId,
        onSelect: onSelectData,
    }

    return (
        <>
            <Header
                title="Painel Analítico da Obra"
                description={`${empreendimentoLabel} — ${obraLabel}`}
                icon={BuildingOffice2Icon}
                hasAction={false}
                hasReturn={false}
            />

            <div data-netexperts="true" className="px-6 pt-5 pb-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                        onClick={() => navigate(isInvestorRoute ? '/investidor/investimentos' : `/investimentos/investimento/${empreendimentoId}`)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                        <ArrowLeftIcon className="w-4 h-4" /> Voltar para obras
                    </button>
                    <div className="flex items-center gap-3">
                        {!isInvestorRoute && (
                            <button
                                onClick={abrirModalProcessarDados}
                                disabled={processando}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ArrowPathIcon className={`w-4 h-4 ${processando ? 'animate-spin' : ''}`} />
                                {processando ? 'Processando...' : 'Processar dados'}
                            </button>
                        )}
                        {isInvestorRoute && (
                            <button
                                onClick={logoutInvestidor}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-semibold"
                            >
                                <ArrowLeftOnRectangleIcon className="w-4 h-4" /> Sair
                            </button>
                        )}
                    </div>
                </div>

                {!isInvestorRoute && (
                    <div className="flex flex-wrap gap-2 bg-white border border-gray-100 rounded-2xl p-2 shadow-sm">
                        {[...ADMIN_TABS, ...TABS].map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                onClick={() => setTab(id)}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === id ? 'bg-gray-900 text-brand-yellow' : 'text-gray-500 hover:bg-gray-50'}`}
                            >
                                <Icon className="w-4 h-4" /> {label}
                            </button>
                        ))}
                    </div>
                )}

                {needsPeriodGate ? (
                    <div className="max-w-2xl mx-auto w-full space-y-5 py-4">
                        {isInvestorRoute && (
                            <div className="flex flex-wrap gap-2 bg-white border border-gray-100 rounded-2xl p-2 shadow-sm">
                                {visibleTabs.map(({ id, label, icon: Icon }) => (
                                    <button
                                        key={id}
                                        onClick={() => setTab(id)}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === id ? 'bg-gray-900 text-brand-yellow' : 'text-gray-500 hover:bg-gray-50'}`}
                                    >
                                        <Icon className="w-4 h-4" /> {label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {tab === 'relatorios-obra' && isInvestorRoute ? (
                            renderTab()
                        ) : (
                            <>
                                <div className="text-center space-y-1">
                                    <div className="inline-flex p-3 rounded-2xl bg-gray-900 mb-2">
                                        <BuildingOffice2Icon className="w-6 h-6 text-brand-yellow" />
                                    </div>
                                    <h2 className="text-lg font-bold text-gray-900">{obraLabel}</h2>
                                    <p className="text-sm text-gray-500">{empreendimentoLabel}</p>
                                    <p className="text-sm text-gray-600 pt-2">
                                        Selecione um <b>período cadastrado</b> e confirme para acessar o painel.
                                    </p>
                                </div>

                                <DataExibicaoPicker {...pickerProps} variant="gate" />

                                <button
                                    type="button"
                                    onClick={confirmPeriod}
                                    disabled={!hasPeriod}
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gray-900 text-brand-yellow font-semibold hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Acessar painel <ArrowRightCircleIcon className="w-5 h-5" />
                                </button>
                                {!hasPeriod && (
                                    <p className="text-center text-xs text-rose-500 font-semibold">
                                        Selecione um período cadastrado para continuar.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                ) : isUtilityTab ? (
                    <>
                        {isInvestorRoute && (
                            <div className="flex flex-wrap gap-2 bg-white border border-gray-100 rounded-2xl p-2 shadow-sm">
                                {visibleTabs.map(({ id, label, icon: Icon }) => (
                                    <button
                                        key={id}
                                        onClick={() => setTab(id)}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === id ? 'bg-gray-900 text-brand-yellow' : 'text-gray-500 hover:bg-gray-50'}`}
                                    >
                                        <Icon className="w-4 h-4" /> {label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div
                            className="rounded-2xl p-5 text-white shadow-lg"
                            style={{ backgroundImage: `linear-gradient(to right, ${theme.grad[0]}, ${theme.grad[1]})` }}
                        >
                            <p className="text-xs font-semibold uppercase tracking-wide text-white/90">Área ativa</p>
                            <h3 className="text-xl font-bold mt-1 text-white">{activeTab?.label}</h3>
                            <p className="text-sm text-white/90 mt-1">{TAB_DESCRIPTION[tab]}</p>
                        </div>
                        {renderTab()}
                    </>
                ) : (
                    <>
                        <DataExibicaoPicker {...pickerProps} variant="compact" onApply={applyPeriodSearch} />

                        {coberturaItens.length > 0 && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 -mt-2 px-1 text-[11px] text-gray-400">
                                <span className="font-semibold text-gray-500">Cobertura de dados:</span>
                                {coberturaItens.map(({ k, label }) => (
                                    <span key={k}>
                                        {label}: <b className="text-gray-600">{mesBR(cobertura[k].min)} – {mesBR(cobertura[k].max)}</b>
                                    </span>
                                ))}
                            </div>
                        )}

                        {isInvestorRoute && (
                            <div className="flex flex-wrap gap-2 bg-white border border-gray-100 rounded-2xl p-2 shadow-sm">
                                {visibleTabs.map(({ id, label, icon: Icon }) => (
                                    <button
                                        key={id}
                                        onClick={() => setTab(id)}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === id ? 'bg-gray-900 text-brand-yellow' : 'text-gray-500 hover:bg-gray-50'}`}
                                    >
                                        <Icon className="w-4 h-4" /> {label}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div
                            className="rounded-2xl p-5 text-white shadow-lg"
                            style={{ backgroundImage: `linear-gradient(to right, ${theme.grad[0]}, ${theme.grad[1]})` }}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-white/90">Área ativa</p>
                                    <h3 className="text-xl font-bold mt-1 text-white">{activeTab?.label}</h3>
                                    <p className="text-sm text-white/90 mt-1">{TAB_DESCRIPTION[tab]}</p>
                                    <p className="text-xs font-semibold text-white/90 mt-3 inline-flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
                                        Período: {periodLabel}
                                    </p>
                                </div>
                                <div className="ml-auto w-full sm:w-auto shrink-0 text-right">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Empreendimento</p>
                                    <p className="text-sm font-semibold text-white">{empreendimentoLabel}</p>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-white/80 mt-2">Obra</p>
                                    <p className="text-sm font-semibold text-white">{obraLabel}</p>
                                </div>
                            </div>
                        </div>

                        {renderTab()}
                    </>
                )}
            </div>
        </>
    )
}

export default ObraPainel
